import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { captureRefFromUrl } from "@/lib/referral";
import { toast } from "sonner";
import SmartImage from "@/components/SmartImage";
import PrivacyNotice from "@/components/PrivacyNotice";
import { ShoppingCart, Trash2, ArrowRight, Sparkles, Package, X } from "lucide-react";

export default function Cart() {
  const { items, remove, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [codeInput, setCodeInput] = useState("");
  const [appliedCode, setAppliedCode] = useState("");
  const [refCode, setRefCode] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRefCode(captureRefFromUrl());
  }, []);

  // Auto-remove already-owned books when the user lands on the cart.
  useEffect(() => {
    if (!user || user === false || items.length === 0) return;
    api.get("/library").then((r) => {
      const ownedIds = new Set((r.data || []).map((b) => b.id));
      const overlap = items.filter((it) => ownedIds.has(it.id));
      if (overlap.length > 0) {
        overlap.forEach((it) => remove(it.id));
        toast(`Removed ${overlap.length} book${overlap.length === 1 ? "" : "s"} you already own`);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const bookIds = useMemo(() => items.map((i) => i.id), [items]);

  useEffect(() => {
    if (!user || user === false || bookIds.length === 0) { setQuote(null); return; }
    const payload = { book_ids: bookIds, use_credits: true };
    if (refCode) payload.referral_code = refCode;
    if (appliedCode) payload.discount_code = appliedCode;
    api.post("/cart/quote", payload)
      .then((r) => setQuote(r.data))
      .catch(() => setQuote(null));
  }, [user, bookIds, refCode, appliedCode]);

  const startCheckout = async () => {
    if (!user) {
      navigate("/login", { state: { from: "/cart" } });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        book_ids: bookIds,
        origin_url: window.location.origin,
        use_credits: true,
      };
      if (refCode) payload.referral_code = refCode;
      if (appliedCode) payload.discount_code = appliedCode;
      const { data } = await api.post("/checkout/cart-session", payload);
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  const applyCode = async () => {
    if (!codeInput) return;
    try {
      const { data } = await api.get(`/discount-codes/${codeInput}/validate`);
      if (data.valid) {
        setAppliedCode(codeInput);
        toast.success(`${data.percent}% off applied`);
      } else {
        toast.error("Invalid or expired code");
      }
    } catch {
      toast.error("Could not validate code");
    }
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-empty" className="max-w-3xl mx-auto px-6 md:px-12 py-24 text-center">
        <ShoppingCart className="w-10 h-10 text-terracotta mx-auto mb-6" strokeWidth={1.2} />
        <h1 className="font-serif text-5xl mb-4">Your cart is quiet.</h1>
        <p className="text-muted_ink text-lg mb-8">
          Add a few books and save <span className="text-terracotta font-medium">up to 20%</span> when you buy them together.
        </p>
        <Link to="/browse" className="btn-primary" data-testid="cart-browse-btn">
          Browse the shelf <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16">
      <div className="flex items-baseline justify-between mb-10">
        <div>
          <div className="label-eyebrow text-muted_ink">Your cart</div>
          <h1 className="font-serif text-5xl mt-2">
            {items.length} book{items.length === 1 ? "" : "s"}
          </h1>
        </div>
        <button
          onClick={() => { clear(); toast("Cart cleared"); }}
          className="text-xs uppercase tracking-widest text-muted_ink hover:text-ink"
          data-testid="clear-cart-btn"
        >
          Clear all
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Items */}
        <div className="lg:col-span-8 space-y-4">
          {items.map((it) => {
            const cover = it.cover_url || (it.cover_path ? `${API}/files/${it.cover_path}` : null);
            return (
              <div key={it.id} data-testid={`cart-item-${it.id}`} className="border border-edge bg-white p-4 flex gap-4">
                <Link to={`/book/${it.id}`} className="w-20 h-28 bg-linen flex-shrink-0 overflow-hidden">
                  {cover && <SmartImage src={cover} alt={it.title} className="w-full h-full object-cover" />}
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/book/${it.id}`} className="font-serif text-xl hover:text-terracotta line-clamp-1">
                    {it.title}
                  </Link>
                  <div className="text-sm text-muted_ink italic mt-0.5">by {it.author}</div>
                  <div className="font-serif text-lg mt-3">${Number(it.price).toFixed(2)}</div>
                </div>
                <button
                  data-testid={`remove-${it.id}`}
                  onClick={() => { remove(it.id); toast(`Removed "${it.title}"`); }}
                  className="text-muted_ink hover:text-terracotta self-start"
                  title="Remove"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          {/* Bundle upsell */}
          {items.length < 5 && (
            <div className="border border-terracotta/40 bg-linen p-5 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-terracotta flex-shrink-0 mt-0.5" strokeWidth={1.5} />
              <div>
                <div className="font-serif text-lg">
                  {items.length < 3
                    ? `Add ${3 - items.length} more to unlock 10% off the bundle.`
                    : `Add ${5 - items.length} more to unlock 20% off the bundle.`}
                </div>
                <Link to="/browse" className="text-sm text-terracotta hover:underline mt-1 inline-block">
                  Keep browsing →
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28 border border-edge bg-linen p-6 space-y-4">
            <div className="label-eyebrow flex items-center gap-2">
              <Package className="w-3.5 h-3.5" /> Order summary
            </div>

            <div className="border-t border-edge pt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted_ink">Subtotal ({items.length})</span>
                <span>${(quote?.subtotal ?? items.reduce((s, it) => s + Number(it.price), 0)).toFixed(2)}</span>
              </div>

              {quote && quote.bundle_discount > 0 && (
                <div className="flex justify-between text-terracotta" data-testid="bundle-row">
                  <span>Bundle discount ({quote.bundle_percent}%)</span>
                  <span>−${quote.bundle_discount.toFixed(2)}</span>
                </div>
              )}
              {quote && quote.referral_valid && (
                <div className="flex justify-between text-terracotta" data-testid="cart-referral-row">
                  <span>Referral discount</span>
                  <span>−${quote.referral_discount.toFixed(2)}</span>
                </div>
              )}
              {quote && quote.code_valid && (
                <div className="flex justify-between text-terracotta" data-testid="cart-code-row">
                  <span>Code <span className="font-mono text-xs">{appliedCode}</span> ({quote.code_percent}%)</span>
                  <span>−${quote.code_discount.toFixed(2)}</span>
                </div>
              )}
              {quote && quote.credits_used > 0 && (
                <div className="flex justify-between text-terracotta">
                  <span>Credits applied</span>
                  <span>−${quote.credits_used.toFixed(2)}</span>
                </div>
              )}
            </div>

            <div className="border-t border-edge pt-4 flex justify-between items-baseline">
              <span className="text-muted_ink">You pay</span>
              <span data-testid="cart-total" className="font-serif text-3xl text-terracotta">
                ${(quote?.final ?? items.reduce((s, it) => s + Number(it.price), 0)).toFixed(2)}
              </span>
            </div>

            {/* Discount code */}
            <div className="flex gap-2">
              <input
                data-testid="cart-code-input"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                placeholder="Have a code?"
                className="flex-1 border border-edge bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-ink"
              />
              <button
                data-testid="cart-apply-code-btn"
                onClick={applyCode}
                className="text-xs uppercase tracking-widest border border-ink px-3 py-2 hover:bg-ink hover:text-paper transition-colors"
              >
                Apply
              </button>
            </div>

            {appliedCode && (
              <button
                onClick={() => { setAppliedCode(""); setCodeInput(""); }}
                className="text-xs text-muted_ink hover:text-ink flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Remove code
              </button>
            )}

            <button
              data-testid="cart-checkout-btn"
              onClick={startCheckout}
              disabled={loading}
              className="w-full btn-primary justify-center disabled:opacity-60"
            >
              {loading ? "Starting checkout…" : (
                <>Checkout <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </button>
            <p className="text-[11px] text-muted_ink text-center">
              Secure payment via Stripe. Instant delivery. DRM-free.
            </p>
            <PrivacyNotice
              testid="cart-privacy"
              purpose="Your email and order details are shared with Stripe to process this payment."
            />
          </div>
        </div>
      </div>
    </div>
  );
}
